import type { ReactNode } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table, ToggleCell } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import {
  hasPermissionOnAnyScope,
  type NotificationCategory,
  type UserFromGetSession,
  useUpdateUserSettings,
  useUserSettings,
} from '@/models/user';
import useViewport from '@/utils/useViewport';

import {
  getChannelSelectionStateByCategory,
  type NotificationChannel,
  type SelectionState,
} from './utils/getChannelSelectionStateByCategory';
import { setNotificationSettings } from './utils/setNotificationSettings';

import './NotificationsTab.css';

function selectionStateToBoolean(state: SelectionState): boolean {
  return state === 'selected';
}

function getNextEnabled(state: SelectionState): boolean {
  return state !== 'selected';
}

export const NotificationsTab = ({ user }: { user: UserFromGetSession }) => {
  const { t } = useLingui();
  const { isMobile } = useViewport();
  const { data: userSettings } = useUserSettings(user.id);
  const { mutate: updateUserSettings } = useUpdateUserSettings();

  if (!userSettings) {
    return;
  }

  const userNotificationSettings = userSettings.notifications;
  const channelState = getChannelSelectionStateByCategory(userNotificationSettings);
  const canActOnBehalfOfNonDigitalizedWorkshop = hasPermissionOnAnyScope(
    user,
    'act_on_behalf_of_non_digitalized_workshop'
  );

  const handleToggle = (
    channel: NotificationChannel,
    category: NotificationCategory | 'all',
    currentState: SelectionState
  ) => {
    const enabled = getNextEnabled(currentState);
    const updated = setNotificationSettings(channel, enabled, category, userNotificationSettings);

    updateUserSettings({ userId: user.id, notifications: updated });
  };

  return (
    <Stack gap="1rem">
      <Paragraph color="secondary">
        <Trans id="profile.tabs.notification.set-preferences">
          Set your notifications preferences
        </Trans>
      </Paragraph>
      <Table
        className="notifications-settings-table table--bordered"
        ariaLabel={t({
          id: 'profile.tabs.notification.categories',
          message: 'Notification categories',
        })}
        columnWidths={isMobile ? ['1fr', '80px', '80px'] : ['1fr', '100px', '100px']}
        variant="lines"
        style={{ maxWidth: '600px' }}
      >
        <Header>
          <Row>
            <Column justifyContent="flex-start"></Column>
            <Column justifyContent="center">
              <Trans id="profile.tabs.notification.column.emails">Emails</Trans>
            </Column>
            <Column justifyContent="center">
              <Trans id="profile.tabs.notification.column.inbox">Inbox</Trans>
            </Column>
          </Row>
        </Header>
        <Body>
          <Row>
            <Cell alignItems="flex-start" justifyContent="flex-start">
              <Stack padding="8px 0">
                <Paragraph weight="medium">
                  <Trans id="profile.tabs.notification.enable-all">Enable all notifications</Trans>
                </Paragraph>
              </Stack>
            </Cell>
            <ToggleCell
              isSelected={selectionStateToBoolean(channelState.all.email)}
              onChange={() => handleToggle('email', 'all', channelState.all.email)}
              ariaLabel={t({
                id: 'profile.tabs.notification.aria.enable-all-emails',
                message: 'Enable or disable all email notifications',
              })}
              cellProps={{ justifyContent: 'center' }}
            />
            <ToggleCell
              isSelected={selectionStateToBoolean(channelState.all.in_app)}
              onChange={() => handleToggle('in_app', 'all', channelState.all.in_app)}
              ariaLabel={t({
                id: 'profile.tabs.notification.aria.enable-all-inbox',
                message: 'Enable or disable all inbox notifications',
              })}
              cellProps={{ justifyContent: 'center' }}
            />
          </Row>
          <NotificationCategoryRows
            channelState={channelState}
            handleToggle={handleToggle}
            showNonDigitalizedWorkshopCc={canActOnBehalfOfNonDigitalizedWorkshop}
            ariaLabelEmail={t({
              id: 'profile.tabs.notification.aria.enable-email',
              message: 'Enable or disable email notifications for this category',
            })}
            ariaLabelInbox={t({
              id: 'profile.tabs.notification.aria.enable-inbox',
              message: 'Enable or disable inbox notifications for this category',
            })}
            ariaLabelNonDigitalizedWorkshopCcEmail={t({
              id: 'profile.tabs.notification.aria.enable-non-digitalized-workshop-cc-email',
              message:
                'Enable or disable email copies of messages sent to non-digitalized workshops',
            })}
          />
        </Body>
      </Table>
    </Stack>
  );
};

const NotificationCategoryRows = ({
  channelState,
  handleToggle,
  showNonDigitalizedWorkshopCc,
  ariaLabelEmail,
  ariaLabelInbox,
  ariaLabelNonDigitalizedWorkshopCcEmail,
}: {
  channelState: ReturnType<typeof getChannelSelectionStateByCategory>;
  handleToggle: (
    channel: NotificationChannel,
    category: NotificationCategory | 'all',
    currentState: SelectionState
  ) => void;
  showNonDigitalizedWorkshopCc: boolean;
  ariaLabelEmail: string;
  ariaLabelInbox: string;
  ariaLabelNonDigitalizedWorkshopCcEmail: string;
}) => (
  <>
    <NotificationCategoryRow
      id="new_tasks"
      channelState={channelState.new_tasks}
      handleToggle={handleToggle}
      ariaLabelEmail={ariaLabelEmail}
      ariaLabelInbox={ariaLabelInbox}
      label={<Trans id="profile.tabs.notification.new-tasks">New tasks</Trans>}
      description={
        <Trans id="profile.tabs.notification.new-tasks.description">
          When you have a new task to handle
        </Trans>
      }
    />
    <NotificationCategoryRow
      id="task_reminders"
      channelState={channelState.task_reminders}
      handleToggle={handleToggle}
      ariaLabelEmail={ariaLabelEmail}
      ariaLabelInbox={ariaLabelInbox}
      label={<Trans id="profile.tabs.notification.task-reminders">Task reminders</Trans>}
      description={
        <Trans id="profile.tabs.notification.task-reminders.description">
          When you have a task to handle by today
        </Trans>
      }
    />
    <NotificationCategoryRow
      id="task_alerts"
      channelState={channelState.task_alerts}
      handleToggle={handleToggle}
      ariaLabelEmail={ariaLabelEmail}
      ariaLabelInbox={ariaLabelInbox}
      label={<Trans id="profile.tabs.notification.task-alerts">Task alerts</Trans>}
      description={
        <Trans id="profile.tabs.notification.task-alerts.description">
          When a task you need to handle is overdue
        </Trans>
      }
    />
    <NotificationCategoryRow
      id="new_comments"
      channelState={channelState.new_comments}
      handleToggle={handleToggle}
      ariaLabelEmail={ariaLabelEmail}
      ariaLabelInbox={ariaLabelInbox}
      label={<Trans id="profile.tabs.notification.new-comments">New comments</Trans>}
      description={
        <Trans id="profile.tabs.notification.new-comments.description">
          When someone comments on a request or shipment you are involved in
        </Trans>
      }
    />
    <NotificationCategoryRow
      id="new_assignations"
      channelState={channelState.new_assignations}
      handleToggle={handleToggle}
      ariaLabelEmail={ariaLabelEmail}
      ariaLabelInbox={ariaLabelInbox}
      label={<Trans id="profile.tabs.notification.new-assignations">New assignations</Trans>}
      description={
        <Trans id="profile.tabs.notification.new-assignations.description">
          When someone assigns you on a request
        </Trans>
      }
    />
    <NotificationCategoryRow
      id="new_events"
      channelState={channelState.new_events}
      handleToggle={handleToggle}
      ariaLabelEmail={ariaLabelEmail}
      ariaLabelInbox={ariaLabelInbox}
      label={<Trans id="profile.tabs.notification.new-events">New events</Trans>}
      description={
        <Trans id="profile.tabs.notification.new-events.description">
          When workflow events occur (issues, refusals, step updates...)
        </Trans>
      }
    />
    {showNonDigitalizedWorkshopCc && (
      <NotificationCategoryRow
        id="non_digitalized_workshop_cc"
        channelState={channelState.non_digitalized_workshop_cc}
        handleToggle={handleToggle}
        ariaLabelEmail={ariaLabelNonDigitalizedWorkshopCcEmail}
        emailOnly
        label={
          <Trans id="profile.tabs.notification.non-digitalized-workshop-cc">
            Non-digitalized workshop emails
          </Trans>
        }
        description={
          <Trans id="profile.tabs.notification.non-digitalized-workshop-cc.description">
            When an email is sent to a non-digitalized workshop you act on behalf of
          </Trans>
        }
      />
    )}
  </>
);

const NotificationCategoryRow = ({
  id,
  channelState,
  handleToggle,
  label,
  description,
  ariaLabelEmail,
  ariaLabelInbox,
  emailOnly = false,
}: {
  id: NotificationCategory;
  channelState: { email: SelectionState; in_app: SelectionState };
  handleToggle: (
    channel: NotificationChannel,
    category: NotificationCategory | 'all',
    currentState: SelectionState
  ) => void;
  label: ReactNode;
  description: ReactNode;
  ariaLabelEmail: string;
  ariaLabelInbox?: string;
  emailOnly?: boolean;
}) => (
  <Row>
    <Cell alignItems="flex-start" justifyContent="flex-start">
      <Stack padding="8px 0">
        <Paragraph weight="medium">{label}</Paragraph>
        <Paragraph size="200" color="secondary">
          {description}
        </Paragraph>
      </Stack>
    </Cell>
    <ToggleCell
      isSelected={selectionStateToBoolean(channelState.email)}
      onChange={() => handleToggle('email', id, channelState.email)}
      ariaLabel={ariaLabelEmail}
      cellProps={{ justifyContent: 'center' }}
    />
    {emailOnly ? (
      <Cell justifyContent="center" />
    ) : (
      <ToggleCell
        isSelected={selectionStateToBoolean(channelState.in_app)}
        onChange={() => handleToggle('in_app', id, channelState.in_app)}
        ariaLabel={ariaLabelInbox ?? ''}
        cellProps={{ justifyContent: 'center' }}
      />
    )}
  </Row>
);
